/**
 * Organization Type Definitions
 * Represents companies, consultancies, and certification bodies in the HALMAS system
 */

/**
 * Organization types in the HALMAS ecosystem
 */
export enum OrganizationType {
  COMPANY = 'COMPANY',                    // Companies seeking halal certification
  CONSULTANCY = 'CONSULTANCY',            // Halal consultancy firms
  CERTIFICATION_BODY = 'CERTIFICATION_BODY', // Certification bodies (e.g., MAIS)
}

/**
 * Organization status
 */
export enum OrganizationStatus {
  ACTIVE = 'ACTIVE',
  INACTIVE = 'INACTIVE',
  SUSPENDED = 'SUSPENDED',
  PENDING_APPROVAL = 'PENDING_APPROVAL',
}

/**
 * Business/Industry sectors
 */
export enum IndustrySector {
  FOOD_MANUFACTURING = 'FOOD_MANUFACTURING',
  FOOD_SERVICE = 'FOOD_SERVICE',
  LOGISTICS = 'LOGISTICS',
  PACKAGING = 'PACKAGING',
  INGREDIENTS = 'INGREDIENTS',
  RETAIL = 'RETAIL',
  PHARMACEUTICALS = 'PHARMACEUTICALS',
  COSMETICS = 'COSMETICS',
  OTHER = 'OTHER',
}

/**
 * Address information
 */
export interface Address {
  street: string;
  city: string;
  state: string;
  postalCode: string;
  country: string;
}

/**
 * Contact person information
 */
export interface ContactPerson {
  name: string;
  position: string;
  email: string;
  phone: string;
  mobile?: string;
}

/**
 * Base organization interface
 */
export interface Organization {
  id: string;
  name: string;
  type: OrganizationType;
  status: OrganizationStatus;

  // Business details
  registrationNumber: string;
  taxId?: string;
  industry: IndustrySector;
  yearEstablished: number;
  numberOfEmployees?: number;
  website?: string;

  // Contact information
  primaryContact: ContactPerson;
  businessAddress: Address;

  // Additional contacts
  additionalContacts?: ContactPerson[];

  // Metadata
  createdAt: Date;
  updatedAt: Date;
  createdBy: string;
  updatedBy?: string;
}

/**
 * Company-specific information (extends Organization)
 */
export interface Company extends Organization {
  type: OrganizationType.COMPANY;

  // Halal readiness status
  readinessScore?: number; // 0-100
  readinessAssessmentDate?: Date;

  // Certification status
  isCertified: boolean;
  currentCertificateId?: string;
  certificationHistory?: string[]; // Array of certificate IDs

  // Assigned consultant
  assignedConsultantId?: string;
  consultantAssignedDate?: Date;
}

/**
 * Consultancy-specific information (extends Organization)
 */
export interface Consultancy extends Organization {
  type: OrganizationType.CONSULTANCY;

  // Consultancy credentials
  licenseNumber: string;
  licenseExpiryDate: Date;
  accreditationBody: string;

  // Service areas
  serviceAreas: IndustrySector[];

  // Statistics
  activeClients: number;
  completedAudits: number;
  successRate?: number; // Percentage of clients certified
}

/**
 * Certification Body-specific information (extends Organization)
 */
export interface CertificationBody extends Organization {
  type: OrganizationType.CERTIFICATION_BODY;

  // Authority information
  authorityNumber: string;
  accreditationStandard: string; // e.g., "MHMS 2020"

  // Jurisdiction
  jurisdictionCountries: string[];
  jurisdictionStates?: string[];

  // Statistics
  certificatesIssued: number;
  activeCertificates: number;
  auditorsCount: number;
}

/**
 * Type guard to check if organization is a Company
 */
export function isCompany(org: Organization): org is Company {
  return org.type === OrganizationType.COMPANY;
}

/**
 * Type guard to check if organization is a Consultancy
 */
export function isConsultancy(org: Organization): org is Consultancy {
  return org.type === OrganizationType.CONSULTANCY;
}

/**
 * Type guard to check if organization is a Certification Body
 */
export function isCertificationBody(org: Organization): org is CertificationBody {
  return org.type === OrganizationType.CERTIFICATION_BODY;
}
